import { useCallback, useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CreditCard, CheckCircle2, XCircle, RefreshCw, ExternalLink, Grid2X2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { useSportsAuth, sportsApi } from '../SportsAuthContext';
import SportsNavBar from '../components/SportsNavBar';

const fmtPhp = (n) => `₱${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0 })}`;

const STATUS_STYLE = {
  trial: { label: 'FREE TRIAL', color: '#00BCD4' },
  active: { label: 'ACTIVE', color: '#00E676' },
  grace_period: { label: 'GRACE PERIOD — RENEW NOW', color: '#FF9800' },
  suspended: { label: 'SUSPENDED', color: '#E63946' },
  deactivated: { label: 'DEACTIVATED', color: '#E63946' },
};

const fmtDay = (iso) => {
  if (!iso) return '';
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return isNaN(d) ? String(iso).slice(0, 10) : d.toLocaleDateString('en-PH', { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function SportsBillingPage() {
  const { organization } = useSportsAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paying, setPaying] = useState(false);
  const [invoice, setInvoice] = useState(null); // {invoice_id, checkout_url, amount_php}
  const [payState, setPayState] = useState(null); // 'polling' | 'paid' | 'expired'
  const [payError, setPayError] = useState(null);
  const pollRef = useRef(null);

  const [history, setHistory] = useState(null);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await sportsApi.get('/sports/billing/status');
      setStatus(data);
    } catch {
      toast.error('Could not load billing status');
    } finally {
      setLoading(false);
    }
    try {
      const { data } = await sportsApi.get('/sports/billing/history');
      setHistory(data.invoices || []);
    } catch {
      setHistory(null); // section shows its own unavailable note
    }
  }, []);

  useEffect(() => { loadStatus(); }, [loadStatus]);

  const stopPolling = () => { if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };

  const pollInvoice = useCallback((invoiceId) => {
    stopPolling();
    setPayState('polling');
    pollRef.current = setInterval(async () => {
      try {
        const { data } = await sportsApi.get(`/sports/billing/invoice/${invoiceId}`);
        if (data.last_payment_error) setPayError(data.last_payment_error);
        if (data.status === 'paid') {
          stopPolling();
          setPayState('paid');
          setPayError(null);
          toast.success('Payment received — subscription renewed!');
          loadStatus();
        } else if (data.status === 'expired') {
          stopPolling();
          setPayState('expired');
        }
      } catch { /* keep polling */ }
    }, 4000);
  }, [loadStatus]);

  useEffect(() => () => stopPolling(), []);

  // Resume polling if we returned from PayMongo with ?invoice=
  useEffect(() => {
    const inv = searchParams.get('invoice');
    if (inv) {
      setInvoice({ invoice_id: inv });
      pollInvoice(inv);
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, pollInvoice, setSearchParams]);

  const startRenewal = async () => {
    setPaying(true);
    setPayError(null);
    try {
      const { data } = await sportsApi.post('/sports/billing/renew', {
        return_url: `${window.location.origin}/sports/billing`,
      });
      setInvoice(data);
      window.open(data.checkout_url, '_blank', 'noopener');
      pollInvoice(data.invoice_id);
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === 'object' ? d.message : (d || 'Could not start renewal'));
    } finally {
      setPaying(false);
    }
  };

  const st = STATUS_STYLE[status?.account_status] || STATUS_STYLE.active;
  const daysLeft = status?.days_left;
  const periodLabel = status?.in_trial ? 'Trial ends' : 'Next renewal';
  const perCourt = status?.pricing_mode === 'per_court';

  return (
    <div className="min-h-screen" style={{ background: '#0A0A0A', color: '#fff', fontFamily: 'Manrope, sans-serif' }}>
      <SportsNavBar />
      <main className="max-w-4xl mx-auto px-5 py-10">
        <p className="text-xs uppercase tracking-widest mb-1" style={{ color: '#737373' }}>Subscription</p>
        <h1 className="text-4xl sm:text-5xl uppercase tracking-tight mb-8" style={{ fontFamily: 'Oswald, sans-serif', fontWeight: 700 }}>
          Billing
        </h1>

        {loading ? (
          <p style={{ color: '#737373' }}>Loading…</p>
        ) : !status ? (
          <p style={{ color: '#737373' }}>Billing status unavailable.</p>
        ) : (
          <>
            {/* ── Grace banner: the trial just converted, or a renewal lapsed.
                 Bookings still work — say so plainly, with the real deadline,
                 so the owner knows exactly how long they have. ── */}
            {status.in_grace && (
              <div data-testid="billing-grace-banner" className="rounded-xl p-4 mb-6 flex items-start gap-3"
                style={{ background: '#1A1206', border: '1px solid #FF980055' }}>
                <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#FF9800' }} />
                <div>
                  <p className="text-sm font-bold" style={{ color: '#FF9800' }}>
                    {status.just_converted_from_trial
                      ? 'Your free trial has ended — renew to keep going'
                      : 'Your subscription has lapsed — renew to keep going'}
                  </p>
                  <p className="text-xs mt-1" style={{ color: '#D9C7A8', lineHeight: 1.6 }}>
                    Your booking page is <strong style={{ color: '#fff' }}>still live</strong> and taking bookings
                    {status.grace_ends ? <> until <strong style={{ color: '#fff' }}>{fmtDay(status.grace_ends)}</strong></> : ''}.
                    {' '}Pay below to continue without interruption — nothing is charged automatically.
                  </p>
                </div>
              </div>
            )}

            {/* ── Status cards ── */}
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
              <div data-testid="billing-status-card" className="rounded-xl p-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Account</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Oswald, sans-serif', color: st.color }}>{status.is_free ? 'FOUNDER — FREE' : st.label}</p>
                {status.period_end && (
                  <p className="text-xs mt-1" style={{ color: '#A3A3A3' }}>
                    {periodLabel}: <span style={{ color: '#fff' }}>{fmtDay(status.period_end)}</span>
                    {typeof daysLeft === 'number' && (
                      <span style={{ color: daysLeft <= 3 ? '#FFB300' : '#737373' }}> ({daysLeft < 0 ? 'expired' : `${daysLeft}d left`})</span>
                    )}
                  </p>
                )}
                {status.last_renewed_at && (
                  <p className="text-xs mt-1" style={{ color: '#737373' }} data-testid="billing-last-renewed">
                    Last renewed: {fmtDay(status.last_renewed_at)}
                  </p>
                )}
              </div>
              <div data-testid="billing-amount-card" className="rounded-xl p-5" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Amount due at renewal</p>
                <p className="text-xl font-bold" style={{ fontFamily: 'Oswald, sans-serif', color: '#fff' }}>{status.is_free ? '—' : fmtPhp(status.amount_due_php)}</p>
                <p className="text-xs mt-1" style={{ color: '#A3A3A3' }}>
                  {perCourt
                    ? `${Math.max(1, status.billable_courts)} billable court${Math.max(1, status.billable_courts) !== 1 ? 's' : ''} × ${fmtPhp(status.per_court_rate)}/mo`
                    : 'Custom monthly rate'}
                </p>
              </div>
              <div data-testid="billing-renew-card" className="rounded-xl p-5 flex flex-col justify-between" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <p className="text-xs uppercase tracking-wide mb-2" style={{ color: '#555' }}>Renew subscription</p>
                {status.is_free ? (
                  <p className="text-sm" style={{ color: '#00E676' }}>Founder accounts never expire.</p>
                ) : (
                  <button data-testid="billing-renew-btn" onClick={startRenewal} disabled={paying || status.amount_due_php <= 0}
                    className="w-full flex items-center justify-center gap-2 rounded-lg py-2.5 text-sm font-bold uppercase transition-transform active:scale-95"
                    style={{ background: '#E63946', color: '#fff', opacity: paying || status.amount_due_php <= 0 ? 0.5 : 1, cursor: paying ? 'wait' : 'pointer' }}>
                    <CreditCard className="h-4 w-4" /> {paying ? 'Preparing…' : `Pay ${fmtPhp(status.amount_due_php)}`}
                  </button>
                )}
                {!status.is_free && status.amount_due_php <= 0 && (
                  <p className="text-xs mt-2" style={{ color: '#FFB300' }}>No price set yet — contact support.</p>
                )}
                {!status.is_free && (
                  <p className="text-xs mt-2" style={{ color: '#555' }}>
                    Renewals are manual — nothing is charged automatically.
                  </p>
                )}
              </div>
            </div>

            {/* ── Payment progress ── */}
            {invoice && payState && (
              <div data-testid="billing-payment-panel" className="rounded-xl p-5 mb-8" style={{ background: '#101010', border: `1px solid ${payState === 'paid' ? '#00E67644' : payState === 'expired' ? '#E6394644' : '#2A2A2A'}` }}>
                {payState === 'polling' && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <RefreshCw className="h-4 w-4 animate-spin" style={{ color: '#FFB300' }} />
                      <p className="text-sm font-bold" style={{ color: '#FFB300' }}>Waiting for payment…</p>
                    </div>
                    <p className="text-xs" style={{ color: '#A3A3A3' }}>
                      Complete the payment in the PayMongo tab. This page updates automatically.
                    </p>
                    {invoice.checkout_url && (
                      <a href={invoice.checkout_url} target="_blank" rel="noreferrer" data-testid="billing-reopen-checkout"
                        className="inline-flex items-center gap-1.5 mt-3 text-xs font-bold" style={{ color: '#2979FF' }}>
                        <ExternalLink className="h-3.5 w-3.5" /> Re-open payment page
                      </a>
                    )}
                    {payError && (
                      <div data-testid="billing-payment-declined" className="flex items-start gap-2 mt-3 rounded-lg p-3" style={{ background: '#1A0505', border: '1px solid #E6394644' }}>
                        <XCircle className="h-4 w-4 shrink-0 mt-0.5" style={{ color: '#E63946' }} />
                        <p className="text-xs" style={{ color: '#E63946' }}>Last attempt declined: {payError} — you can retry on the payment page.</p>
                      </div>
                    )}
                  </div>
                )}
                {payState === 'paid' && (
                  <div className="flex items-center gap-2" data-testid="billing-payment-success">
                    <CheckCircle2 className="h-5 w-5" style={{ color: '#00E676' }} />
                    <p className="text-sm font-bold" style={{ color: '#00E676' }}>Payment received — your subscription is active for another 30 days.</p>
                  </div>
                )}
                {payState === 'expired' && (
                  <div className="flex items-center gap-2" data-testid="billing-payment-expired">
                    <AlertTriangle className="h-5 w-5" style={{ color: '#E63946' }} />
                    <p className="text-sm" style={{ color: '#E63946' }}>This payment session expired. Click "Pay" again to start a new one.</p>
                  </div>
                )}
              </div>
            )}

            {/* ── Court usage breakdown ── */}
            {perCourt && (
              <div data-testid="billing-court-breakdown" className="rounded-xl overflow-hidden" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
                  <Grid2X2 className="h-4 w-4" style={{ color: '#E63946' }} />
                  <h2 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Court Usage (last 30 days)</h2>
                </div>
                <div className="px-5 py-3">
                  <p className="text-xs mb-3" style={{ color: '#737373' }}>
                    Courts with fewer than {status.billable_min_bookings} bookings this cycle are <span style={{ color: '#00E676', fontWeight: 700 }}>FREE</span> — you only pay for courts that earn. Minimum 1 court per cycle.
                  </p>
                  {(status.court_breakdown || []).length === 0 ? (
                    <p className="text-sm pb-3" style={{ color: '#555' }}>No active courts yet.</p>
                  ) : (
                    <div className="space-y-2 pb-3">
                      {status.court_breakdown.map(ct => (
                        <div key={ct.court_id} data-testid={`billing-court-row-${ct.court_id}`}
                          className="flex items-center justify-between rounded-lg px-4 py-3" style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}>
                          <div className="min-w-0">
                            <p className="text-sm font-bold truncate" style={{ color: '#fff' }}>{ct.name}</p>
                            <p className="text-xs" style={{ color: '#737373' }}>{ct.bookings} booking{ct.bookings !== 1 ? 's' : ''} this cycle</p>
                          </div>
                          {ct.billable ? (
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: '#E6394618', color: '#E63946', border: '1px solid #E6394640' }}>
                              BILLABLE · {fmtPhp(status.per_court_rate)}
                            </span>
                          ) : (
                            <span className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0" style={{ background: '#00E67614', color: '#00E676', border: '1px solid #00E67630' }}>
                              FREE THIS CYCLE
                            </span>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* ── Payment history ── */}
            {!status.is_free && (
              <div data-testid="billing-history" className="rounded-xl overflow-hidden mt-8" style={{ background: '#141414', border: '1px solid #1E1E1E' }}>
                <div className="flex items-center gap-2 px-5 py-4" style={{ borderBottom: '1px solid #1E1E1E' }}>
                  <CreditCard className="h-4 w-4" style={{ color: '#E63946' }} />
                  <h2 className="text-base font-bold uppercase" style={{ fontFamily: 'Oswald, sans-serif' }}>Payment History</h2>
                </div>
                <div className="px-5 py-4">
                  {history === null ? (
                    <p className="text-sm" style={{ color: '#555' }}>Payment history is unavailable right now — try refreshing.</p>
                  ) : history.length === 0 ? (
                    <p className="text-sm" style={{ color: '#555' }}>
                      No online payments yet. When you renew here, each payment appears in this list.
                    </p>
                  ) : (
                    <div className="space-y-2">
                      {history.map(inv => (
                        <div key={inv.id} data-testid={`billing-history-row-${inv.id}`}
                          className="flex items-center justify-between rounded-lg px-4 py-3" style={{ background: '#0D0D0D', border: '1px solid #1A1A1A' }}>
                          <div className="min-w-0">
                            <p className="text-sm font-bold" style={{ color: '#fff' }}>{fmtPhp(inv.amount_php)}</p>
                            <p className="text-xs" style={{ color: '#737373' }}>
                              {fmtDay(inv.paid_at || inv.created_at)}
                              {inv.pricing_mode === 'per_court' && inv.court_count ? ` · ${inv.court_count} court${inv.court_count !== 1 ? 's' : ''}` : ''}
                            </p>
                          </div>
                          <span className="text-xs font-bold px-2.5 py-1 rounded-full shrink-0" style={
                            inv.status === 'paid'
                              ? { background: '#00E67614', color: '#00E676', border: '1px solid #00E67630' }
                              : inv.status === 'pending'
                                ? { background: '#FFB30014', color: '#FFB300', border: '1px solid #FFB30030' }
                                : { background: '#1A1A1A', color: '#555', border: '1px solid #2A2A2A' }
                          }>
                            {inv.status === 'paid' ? 'PAID' : inv.status === 'pending' ? 'PENDING' : String(inv.status || '').toUpperCase()}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </main>
    </div>
  );
}
